import { HasId } from "app/shared/model/properties.model";
import { Observable, Subscriber } from "rxjs";
import { share } from "rxjs/operators";
import { GeneralParameters } from "app/shared/model/request-parameter.model";
import { PagedArray } from "app/shared/model/app.model";

export abstract class ServiceEntityNotifier<T extends HasId> {
	protected readonly observers = new Map<number, UpdateController<T>>();
	
	register(entityId: number): Observable<T> {
		const localController = this.observers.get(entityId);
		if (localController) return localController.observable$;
		
		const observable$ = new Observable<T>(subscriber => {
			const controller = new UpdateController(observable$, subscriber);
			
			const current = this.observers.get(entityId);
			if (current && !current.subscriber.closed) {
				subscriber.error(`Will not create duplicate notifier for entity ${entityId}`)
			}
			
			this.observers.set(entityId, controller);
			
			subscriber.add(() => {
				const current = this.observers.get(entityId);
				if (current === controller) this.observers.delete(entityId);
			})
		})
			.pipe(share());
		
		return observable$;
	}
	
	next(...entities: ServiceEntity<T>): void {
		this.nextItems(entities);
	}
	
	nextItems(entities: ServiceEntity<T>) {
		const needsUpdate = new Map<number, UpdateController<T>>();
		
		entities.forEach(entity => {
			if (!entity) return;
			
			const controller = this.observers.get(typeof entity === 'number' ? entity : entity.id);
			if (!controller) return;
			
			if (typeof entity === 'number') {
				needsUpdate.set(entity, controller);
				return;
			}
			
			controller.update(entity);
		});
		
		const ids = Array.from(needsUpdate.entries()).map(entry => entry[0]);
		if (!ids.length) return;
		
		this.getItems({ id: ids }).subscribe(entities => {
			entities.forEach(entity => needsUpdate.get(entity.id)?.update(entity));
		})
	}
	
	protected abstract getItems(query: Pick<GeneralParameters, 'id'>): Observable<PagedArray<T>>;
}

class UpdateController<T extends HasId> {
	constructor(
		public readonly observable$: Observable<T>,
		public readonly subscriber: Subscriber<T>
	) {}
	
	update(value: T) {
		this.subscriber.next(value);
	}
	
	close(error?: Error) {
		if (error) {
			this.subscriber.error(error);
		}
		else {
			this.subscriber.complete();
		}
	}
}

export type ServiceEntity <T extends HasId> = Array<number | T>;